import React, { createContext, useContext, useEffect, useState } from "react";
import ApiService from "../handlers/apiService";
import { EnergyService } from "./EnergyService";
import { UserService } from "./UserService";
import ServicesManager from "./ServicesManager";

interface ServicesContextType {
  userService: UserService | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
}

const ServicesContext = createContext<ServicesContextType>({
  userService: null,
  isInitialized: false,
  isLoading: true,
  error: null,
});

export function useServices() {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error("useServices must be used within a ServicesProvider");
  }
  return context;
}

interface ServicesProviderProps {
  children: React.ReactNode;
}

export function ServicesProvider({ children }: ServicesProviderProps) {
  const [userService, setUserService] = useState<UserService | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeServices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get API service instance
        const apiService = ApiService.getInstance();
        const servicesManager = ServicesManager.getInstance();

        // Initialize UserService FIRST to ensure user exists before other services
        const userServiceInstance = await UserService.initialize(apiService);
        setUserService(userServiceInstance);
        servicesManager.setUserService(userServiceInstance);

        // Initialize EnergyService AFTER user is created
        EnergyService.initialize(apiService);

        setIsInitialized(true);
        console.log("Services initialized successfully");
      } catch (err) {
        console.error("Failed to initialize services:", err);
        setError(
          err instanceof Error ? err.message : "Failed to initialize services"
        );
      } finally {
        setIsLoading(false);
      }
    };

    initializeServices();
  }, []);

  const contextValue: ServicesContextType = {
    userService,
    isInitialized,
    isLoading,
    error,
  };

  return (
    <ServicesContext.Provider value={contextValue}>
      {children}
    </ServicesContext.Provider>
  );
}
